/**
 * `FX-1b`, `F5`: **the vehicle lifecycle's advertised stages , ONE definition.**
 *
 * `02` §2.4.4 gives eight ordered stages plus three terminal branches. Stages 1 to 5
 * are the ones on which a car is still on the forecourt and in front of buyers; stage 6,
 * `transaction`, is where `DECISION ADDED 17` says a hardened lock takes the vehicle
 * « off-market ».
 *
 * **Why this list is here and not in three places.** It was in two already, and they
 * disagreed: the dealer's card carried `ADVERTISED = {enligne, demande, visite, expertise,
 * rapport}` (`vehicle-card.tsx`), and the buyer's search index selected `stage = 'enligne'`
 * alone, so a published car Hakili had moved to `expertise` was advertised on one screen and
 * absent from the other's search. `F5`'s binding is « `published_at` the index's truth and
 * **never a second stage list** », and the way to honour it is one list both sides read ,
 * the same shape `ETAPES_CLOSES` took for the dossier at `FX-1`.
 *
 * **What decides the market is the LOCK, not the stage**, which is the product's own
 * sentence in `catalog/events/availability.handlers.ts`: *« The firm lock is a column, not a
 * stage »*. The buyer's search door reads `held` live from `vehicles.lock_state` and MARKS a
 * held car rather than hiding it. So these five stages say « this car is on the market »,
 * and the lock says « somebody is engaged on it » , two facts, two columns, never merged.
 */

/** `02` §2.4.4, stages 1 to 8 in the record's own order. */
export const VEHICLE_LIFECYCLE_STAGES = [
  "enligne",
  "demande",
  "visite",
  "expertise",
  "rapport",
  "transaction",
  "paye",
  "retire",
] as const;

export type VehicleLifecycleStage = (typeof VEHICLE_LIFECYCLE_STAGES)[number];

/**
 * The first stage at which a car has left the market: `02` §2.4.4's stage 6, where
 * `DECISION ADDED 17`'s hardened lock puts it « off-market ». Everything before it is
 * advertised, and the set below is DERIVED from that one fact rather than re-typed.
 */
export const FIRST_OFF_MARKET_STAGE: VehicleLifecycleStage = "transaction";

/**
 * Stages 1 to 5: `enligne`, `demande`, `visite`, `expertise`, `rapport`.
 *
 * Derived by cutting `VEHICLE_LIFECYCLE_STAGES` at the first off-market stage, so adding a stage to
 * the lifecycle cannot silently leave this set behind.
 */
export const ADVERTISED_STAGES: readonly VehicleLifecycleStage[] = VEHICLE_LIFECYCLE_STAGES.slice(
  0,
  VEHICLE_LIFECYCLE_STAGES.indexOf(FIRST_OFF_MARKET_STAGE),
);

/** Whether a car at this stage is still one a buyer may be shown. A terminal branch is not. */
export function isAdvertisedStage(stage: string): boolean {
  return (ADVERTISED_STAGES as readonly string[]).includes(stage);
}
